import { type FormEvent, useEffect, useState } from "react";

import { appelerApi } from "../api/client";
import type { Catalogue } from "../api/types";
import { ProductionCard } from "../components/ProductionCard";

export function CataloguePage() {
  const [recherche, definirRecherche] = useState("");
  const [discipline, definirDiscipline] = useState("");
  const [publicCible, definirPublicCible] = useState("");
  const [page, definirPage] = useState(1);
  const [catalogue, definirCatalogue] = useState<Catalogue | null>(null);
  const [chargement, definirChargement] = useState(true);
  const [erreur, definirErreur] = useState("");
  const [declencheur, definirDeclencheur] = useState(0);

  useEffect(() => {
    const parametres = new URLSearchParams({
      page: String(page),
      taille_page: "12"
    });
    if (recherche) parametres.set("recherche", recherche);
    if (discipline) parametres.set("discipline", discipline);
    if (publicCible) parametres.set("public", publicCible);
    definirChargement(true);
    definirErreur("");
    appelerApi<Catalogue>(`/catalogue?${parametres}`)
      .then(definirCatalogue)
      .catch((cause: Error) => definirErreur(cause.message))
      .finally(() => definirChargement(false));
  }, [page, declencheur]);

  function rechercher(evenement: FormEvent) {
    evenement.preventDefault();
    definirPage(1);
    definirDeclencheur((valeur) => valeur + 1);
  }

  return (
    <section className="page catalogue-page">
      <header className="page__entete">
        <span className="sur-titre sur-titre--sombre">Catalogue professionnel</span>
        <h1>Des spectacles documentés, pas de simples annonces.</h1>
        <p>
          Filtrez les productions publiées et vérifiez l’origine de chaque résultat
          avant de prendre contact.
        </p>
      </header>
      <form className="filtres" onSubmit={rechercher}>
        <label className="champ champ--recherche">
          <span>Rechercher</span>
          <input
            value={recherche}
            onChange={(e) => definirRecherche(e.target.value)}
            placeholder="Titre, compagnie, artiste…"
          />
        </label>
        <label className="champ">
          <span>Discipline</span>
          <select value={discipline} onChange={(e) => definirDiscipline(e.target.value)}>
            <option value="">Toutes</option>
            <option>Théâtre</option>
            <option>Danse</option>
            <option>Musique</option>
            <option>Cirque</option>
            <option>Humour</option>
            <option>Jeune public</option>
          </select>
        </label>
        <label className="champ">
          <span>Public</span>
          <select value={publicCible} onChange={(e) => definirPublicCible(e.target.value)}>
            <option value="">Tous</option>
            <option>Familial</option>
            <option>Jeune public</option>
            <option>Scolaire</option>
            <option>Tout public</option>
          </select>
        </label>
        <button className="bouton bouton--primaire" type="submit">
          Afficher les résultats
        </button>
      </form>

      {erreur && <p className="alerte alerte--erreur">{erreur}</p>}
      <div className="resultats-meta" aria-live="polite">
        {chargement
          ? "Recherche en cours…"
          : `${catalogue?.pagination.total ?? 0} production(s) trouvée(s)`}
      </div>
      {!chargement && catalogue?.elements.length ? (
        <>
          <div className="grille-productions">
            {catalogue.elements.map((production) => (
              <ProductionCard key={production.id} production={production} />
            ))}
          </div>
          {catalogue.pagination.pages > 1 && (
            <nav className="pagination" aria-label="Pagination du catalogue">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => definirPage((valeur) => valeur - 1)}
              >
                Précédent
              </button>
              <span>
                Page {page} sur {catalogue.pagination.pages}
              </span>
              <button
                type="button"
                disabled={page >= catalogue.pagination.pages}
                onClick={() => definirPage((valeur) => valeur + 1)}
              >
                Suivant
              </button>
            </nav>
          )}
        </>
      ) : (
        !chargement && (
          <div className="etat-vide">
            <strong>Aucune production ne correspond encore.</strong>
            <p>Élargissez les filtres ou revenez bientôt : le catalogue grandit.</p>
          </div>
        )
      )}
    </section>
  );
}
